// Approving a link from Nexus (github.com/mrweaver/nexus): the other end of the
// "Link Khronos" button there. Nexus opened us at /link/nexus with a one-time
// code; approving sends that code to *our* server, which claims it from Nexus
// and starts pushing workouts. The owner then confirms the link back in Nexus.

import React, { useState } from "react";
import { Check, ExternalLink, Link2, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import LoginButton from "../auth/LoginButton";
import { useAuth } from "../../contexts/AuthContext";
import { linkNexus } from "../../services/api";
import { clearPendingNexusLink, getPendingNexusLink } from "../../utils/nexusLink";

const NexusLinkDialog = () => {
  const [request, setRequest] = useState(() => getPendingNexusLink());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [linked, setLinked] = useState(null);
  const { isAuthenticated, user } = useAuth();

  if (!request) return null;

  let host = request.nexus;
  try {
    host = new URL(request.nexus).host;
  } catch {
    /* shown as given */
  }

  const close = () => {
    clearPendingNexusLink();
    setRequest(null);
  };

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await linkNexus(request.nexus, request.code);
      clearPendingNexusLink();
      setLinked(res.link);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" /> Link Khronos to Nexus
          </DialogTitle>
          <DialogDescription>
            {linked
              ? "Linked. One last step back in Nexus."
              : `Nexus at ${host} would like to receive your workouts${request.profile ? ` in the “${request.profile}” profile` : ""}.`}
          </DialogDescription>
        </DialogHeader>

        {linked ? (
          <div className="space-y-3 text-sm">
            <div className="flex gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <span>
                Return to Nexus and confirm the link. Your workouts will start arriving as soon as you do, and every workout
                you save from now on follows automatically.
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p>What Nexus will get, for every workout{isAuthenticated && user?.email ? ` on ${user.email}` : ""}:</p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>when it started and finished, and its title</li>
              <li>your effort rating (RPE), and average and peak heart rate if recorded</li>
            </ul>
            <p className="flex gap-2 text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              Nothing else — not your plans, notes, mates or settings. Unlink any time in Settings → Devices.
            </p>
            {error ? <p className="text-destructive">{error}</p> : null}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 [&>*]:w-full sm:[&>*]:w-auto">
          {linked ? (
            <Button asChild>
              <a href={`${request.nexus}/settings/sources`}>
                <ExternalLink className="h-4 w-4 mr-2" /> Back to Nexus
              </a>
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close} disabled={busy}>
                Not now
              </Button>
              {!isAuthenticated ? (
                <LoginButton>Sign in to link</LoginButton>
              ) : (
                <Button onClick={approve} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Link my workouts
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NexusLinkDialog;
